// janus-engine/ability/hostile-spell-miscast.ts — the HOSTILE SPELL MISCAST roll (layer hostile_spell_miscast_chance_in_encounter,
// 2026-10-07; first carrier: the Thief passive "Walking Punchline"). The rule lives here; its PHP twin
// includes/functions/janus/core/hostile_spell_miscast_roll.php keeps the legacy janus() (every hand-back, R-108) drawing the same dice.
//
// While a creature holding the layer is in the encounter, every SPELL (item_type 'spell', cantrips included — the Armor Bending gate has no
// level floor) cast by a creature on the OPPOSING team (check_opposing_team) may miscast:
//   · holders = encounter members other than the caster, not deleted / dead / at 0 Vitality, opposing the caster, carrying the layer on
//     the member row itself or on an active passive_ability entry (a thin {item_id} entry resolves through the held definition);
//   · two holders do NOT stack: the HIGHEST chance applies (ties → the lowest encounter_creature_id names the holder), capped at 100;
//   · one d100 (ft_rng_die(100)); face <= chance → MISCAST: `spell_miscast` (holder → caster, the spell's id, roll_result = the face) and
//     the spell fails exactly like a failed Armor Bending Roll — no counterspell check, no dispatch, no mana, but the action is spent.
// No range / line of sight: the whole encounter. Rolled right after a PASSED (or absent) armor bending roll — templateSpellRoll.
// A passive definition the page does not hold could carry the layer → hand back pre-draw.
import { type JanusCtx, type Row, ecId, ecLean, handBack, insertRoll, itemField, record, requiredInt, scalar, upper, definitionNotHeld } from './context.js';
import { opposingTeams } from '../reactions/php-row-rules.js';

export const HOSTILE_SPELL_MISCAST_LAYER = 'hostile_spell_miscast_chance_in_encounter';

const present = (v: unknown): boolean => { const s = scalar(v); return s !== null && s !== undefined && s !== '' && s !== 0 && s !== '0' && s !== false; };

/** The member's miscast chance (root layer, else its active passives — the first provider, find_caster_layer_provider_arr order). */
function holderChance(ctx: JanusCtx, member: Row): number {
  const read = (row: Row): number | null => {
    const v = ctx.read.value(row, HOSTILE_SPELL_MISCAST_LAYER);
    return present(v) ? requiredInt(v, 'hostile_spell_miscast:invalid-chance') : null;
  };
  const root = read(member);
  if (root !== null) return root;
  for (const entry of ctx.read.entries(member, 'passive_ability')) {
    const row = record(entry);
    if (row?.passive_conditions_active === false) continue;
    const inner = record(row?.item_arr);
    let def: Row | null = inner && (record(inner.item_layer_types) || record(inner.layers)) ? inner
      : row && record(row.items) && (record(row.item_layer_types) || record(row.layers)) ? row : null;
    if (!def) {
      const id = upper(typeof entry === 'string' ? entry.split('|')[0] : itemField(inner ?? row, 'item_id') ?? itemField(row, 'item_id') ?? row?.item_id);
      if (!id) continue;
      def = ctx.read.definition(id);
      if (!def) definitionNotHeld('hostile_spell_miscast:passive-definition-not-held', id);
    }
    const chance = read(def);
    if (chance !== null) return chance;
  }
  return 0;
}

/** The applying holder and its chance (0 = no roll). No draw; hands back on unprovable state. */
export function hostileSpellMiscastHolder(ctx: JanusCtx, ability: Row, caster: Row): { holder: Row | null; chance: number } {
  if (String(itemField(ability, 'item_type') ?? '') !== 'spell') return { holder: null, chance: 0 };
  const casterId = ecId(caster);
  if (!casterId) return { holder: null, chance: 0 };
  const layer = (row: Row, key: string): unknown => ctx.read.value(row, key);
  let best: { holder: Row | null; chance: number } = { holder: null, chance: 0 };
  for (const member of ctx.members.values()) {
    const id = ecId(member);
    if (!id || id === casterId) continue;
    // (int) casts, as PHP's twin reads them
    const col = (key: string): number => { const n = Math.trunc(Number(scalar(ecLean(member, key)) ?? 0)); return Number.isFinite(n) ? n : 0; };
    if (col('encounter_creature_deleted') === 1 || col('encounter_creature_dead') === 1) continue;
    if (col('v_current') <= 0) continue;
    if (!opposingTeams(member, caster, layer)) continue;
    const chance = Math.min(100, holderChance(ctx, member));
    if (chance <= 0) continue;
    if (chance > best.chance || (chance === best.chance && best.holder && id < ecId(best.holder))) best = { holder: member, chance };
  }
  return best;
}

/** Whether a miscast roll would be made for this cast (no draw) — the persistent-area paths hand it back pre-draw. */
export function hostileSpellMiscastApplies(ctx: JanusCtx, ability: Row, caster: Row): boolean {
  return hostileSpellMiscastHolder(ctx, ability, caster).chance > 0;
}

/** hostile_spell_miscast_roll(): true = MISCAST (the spell fails as a failed armor bending roll does; the caller spends the action).
 *  `plan` is hostileSpellMiscastHolder() re-read after the armor bending roll (PHP's timing); templateSpellRoll also calls it once BEFORE
 *  that d20, so every definition it needs is proven pre-draw. */
export function hostileSpellMiscastRoll(ctx: JanusCtx, plan: { holder: Row | null; chance: number }, caster: Row, abilityId: string): boolean {
  const { holder, chance } = plan;
  if (!holder || chance <= 0) return false;
  const face = ctx.roll.d(100);
  if (face > chance) return false;
  insertRoll(ctx, { roll_type_1: 'spell_miscast', roll_item_id: upper(abilityId), by: holder, to: caster, roll_result: face });
  return true;
}
